import { useCallback, useEffect, useState } from "react";
import { GomakePrimaryButton, GomakeTextInput } from "@/components";
import { useGomakeAxios, useGomakeRouter } from "@/hooks";
import {
  requestInviteOtpApi,
  verifyInviteOtpApi,
  acceptInvitationApi,
} from "@/services/api-service/users/users-api";
import { useStyle } from "./style";

interface NativeSignupFormProps {
  inviteToken: string;
  /** Prefilled from the invite; the invitee confirms it (must match their invited email). */
  defaultEmail?: string;
  onError: (message: string) => void;
}

type Step = "email" | "otp" | "password";

const OTP_LENGTH = 6;
const MIN_PASSWORD_LENGTH = 8;

/**
 * Reads the OTP-verification JWT from the verify response. The gateway wraps the CRM body a
 * couple of times ({ data: { data: { otpVerificationJwt } } }) and casing varies, so search
 * for the key anywhere in the object.
 */
const findStringKeyDeep = (
  obj: any,
  targetLower: string,
  depth = 0,
): string | undefined => {
  if (!obj || typeof obj !== "object" || depth > 6) return undefined;
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (key.toLowerCase() === targetLower && typeof val === "string" && val) {
      return val;
    }
  }
  for (const key of Object.keys(obj)) {
    const found = findStringKeyDeep(obj[key], targetLower, depth + 1);
    if (found) return found;
  }
  return undefined;
};

const extractOtpJwt = (res: any): string | undefined =>
  findStringKeyDeep(res, "otpverificationjwt");

/** Best-effort human-readable error from a (loosely typed) API response. */
const getErrorMessage = (res: any, fallback: string): string =>
  res?.message ||
  res?.msg ||
  (typeof res?.data === "string" ? res.data : undefined) ||
  res?.data?.message ||
  fallback;

/**
 * Native GoMake sign-up for brand-new invitees (Cognito self sign-up is disabled). Verifies
 * email ownership via OTP, then creates the account with a chosen password server-side
 * (admin-create) and sends the user to sign in.
 */
const NativeSignupForm = ({
  inviteToken,
  defaultEmail,
  onError,
}: NativeSignupFormProps) => {
  const { clasess } = useStyle();
  const { callApi } = useGomakeAxios();
  const { navigate } = useGomakeRouter();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [otp, setOtp] = useState("");
  const [otpJwt, setOtpJwt] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (defaultEmail && !email) setEmail(defaultEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail]);

  const sendOtp = useCallback(async () => {
    onError("");
    if (!email.trim()) {
      onError("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestInviteOtpApi(callApi, () => {}, {
        token: inviteToken,
        email: email.trim(),
      });
      if (res?.success) {
        setStep("otp");
      } else {
        onError(getErrorMessage(res, "Could not send the verification code."));
      }
    } finally {
      setSubmitting(false);
    }
  }, [callApi, email, inviteToken, onError]);

  const verifyOtp = useCallback(
    async (code: string) => {
      onError("");
      setSubmitting(true);
      try {
        const res = await verifyInviteOtpApi(callApi, () => {}, {
          token: inviteToken,
          email: email.trim(),
          otp: code,
        });
        const jwt = extractOtpJwt(res);
        if (jwt) {
          setOtpJwt(jwt);
          setStep("password");
        } else {
          setOtp("");
          onError(getErrorMessage(res, "Invalid or expired code."));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [callApi, email, inviteToken, onError],
  );

  // Auto-verify once the full code is entered.
  useEffect(() => {
    if (step === "otp" && otp.length === OTP_LENGTH && !submitting) {
      verifyOtp(otp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, step]);

  const submitPassword = useCallback(async () => {
    onError("");
    if (password.length < MIN_PASSWORD_LENGTH) {
      onError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      onError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await acceptInvitationApi(callApi, () => {}, {
        token: inviteToken,
        password,
        otpVerificationJwt: otpJwt,
      });
      if (res?.success) {
        // Account created — send the user to sign in with the password they just set.
        navigate("/login");
      } else {
        onError(getErrorMessage(res, "Could not complete sign up."));
      }
    } finally {
      setSubmitting(false);
    }
  }, [callApi, confirmPassword, inviteToken, navigate, onError, otpJwt, password]);

  const onKeyDown = (handler: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handler();
  };

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
      {step === "email" && (
        <>
          <div style={clasess.inputContainer}>
            <div style={clasess.inputLbl}>Email</div>
            <GomakeTextInput
              id="invite-email"
              name="email"
              type="email"
              value={email}
              placeholder="you@example.com"
              onChange={(e: any) => setEmail(e.target.value)}
              onKeyDown={onKeyDown(sendOtp)}
              autoComplete="email"
            />
          </div>
          <GomakePrimaryButton onClick={sendOtp} disabled={submitting}>
            Sign up with GoMake
          </GomakePrimaryButton>
        </>
      )}

      {step === "otp" && (
        <>
          <div style={clasess.inputContainer}>
            <div style={clasess.inputLbl}>
              Enter the {OTP_LENGTH}-digit code sent to {email}
            </div>
            <GomakeTextInput
              id="invite-otp"
              name="otp"
              value={otp}
              placeholder="______"
              onChange={(e: any) =>
                setOtp(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))
              }
              inputProps={{ inputMode: "numeric", maxLength: OTP_LENGTH }}
              autoComplete="one-time-code"
            />
          </div>
          <GomakePrimaryButton onClick={sendOtp} disabled={submitting}>
            Resend code
          </GomakePrimaryButton>
        </>
      )}

      {step === "password" && (
        <>
          <div style={clasess.inputContainer}>
            <div style={clasess.inputLbl}>Password</div>
            <GomakeTextInput
              id="invite-password"
              name="password"
              type="password"
              value={password}
              placeholder="At least 8 characters"
              onChange={(e: any) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div style={clasess.inputContainer}>
            <div style={clasess.inputLbl}>Confirm password</div>
            <GomakeTextInput
              id="invite-confirm-password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              placeholder="Re-enter your password"
              onChange={(e: any) => setConfirmPassword(e.target.value)}
              onKeyDown={onKeyDown(submitPassword)}
              autoComplete="new-password"
            />
          </div>
          <GomakePrimaryButton onClick={submitPassword} disabled={submitting}>
            Sign up
          </GomakePrimaryButton>
        </>
      )}
    </div>
  );
};

export { NativeSignupForm };
